/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { BaseCommand } from "@vienna-hypertext/observable";
import { v4 as uuidv4 } from "uuid";

import { Block } from "../block";
import { BlockFactory } from "../block/BlockFactory";
import { Plate } from "src/classes/plate/Plate";
import { ArrowSession, DragBlockSession, ResizeSession } from "src/classes/plate/Session";

export type Command = MoveCommand | ResizeCommand | SelectCommand | CreateCommand | DeleteCommand;

export type CommandType = "MOVE" | "RESIZE" | "CREATE" | "DELETE" | "SELECT";

/* -------------------------------------------------- */
/*                        MOVE                        */
/* -------------------------------------------------- */

export class MoveCommand extends BaseCommand {
  readonly type = "MOVE";

  readonly blocks: {
    blockId: string;
    from: {
      top: number;
      left: number;
    };
    to: {
      top: number;
      left: number;
    };
  }[];

  plate: Plate;

  constructor(moveSession: DragBlockSession | ArrowSession, plate: Plate) {
    super();

    this.plate = plate;

    this.blocks = moveSession.initialBlockSnapshots.map((b) => {
      const block = this.plate.getBlockById(b.id)!;
      const newTop = block.snapshot.top;
      const newLeft = block.snapshot.left;

      const prevTop = b.top;
      const prevLeft = b.left;

      return {
        blockId: b.id,
        from: { top: prevTop, left: prevLeft },
        to: { top: newTop, left: newLeft },
      };
    });
  }

  undo(): void {
    this.blocks.forEach((blockPosition) => {
      const block = this.plate.getBlockById(blockPosition.blockId)!;
      block.move(blockPosition.from.top, blockPosition.from.left);
    });
  }

  redo(): void {
    this.blocks.forEach((blockPosition) => {
      const block = this.plate.getBlockById(blockPosition.blockId)!;
      block.move(blockPosition.to.top, blockPosition.to.left);
    });
  }

  /**
   * Checks if new and old positions are equal
   */
  blocksMoved(): boolean {
    if (!this.blocks[0]) return false;
    return (
      this.blocks[0].to.top !== this.blocks[0].from.top || this.blocks[0].to.left !== this.blocks[0].from.left
    );
  }
}

/* -------------------------------------------------- */
/*                        SELECT                      */
/* -------------------------------------------------- */

export class SelectCommand extends BaseCommand {
  readonly type = "SELECT";

  selectedIds: string[];

  deselectedIds: string[];

  plate: Plate;

  constructor(currentSelection: string[], newSelection: string[], plate: Plate) {
    super();
    this.selectedIds = newSelection.filter((id) => !currentSelection.includes(id));
    this.deselectedIds = currentSelection.filter((id) => !newSelection.includes(id));
    this.plate = plate;
  }

  undo(): void {
    // 1. Get the ids that were selected and deselected
    const idsToDeselect = this.selectedIds;
    const idsToSelect = this.deselectedIds;

    // 2. Filter the current selection to remove the ids that were selected
    const draftSelection = this.plate.snapshot.selection.filter((blockId) => !idsToDeselect.includes(blockId));

    // 3. Add the ids that were deselected to the draft selection
    const finalSelection = [...draftSelection, ...idsToSelect];

    // 4. Update the selection
    this.plate.select(finalSelection, { addToStack: false });
  }

  redo(): void {
    // 1. Get the ids that were selected and deselected
    const idsToDeselect = this.deselectedIds;
    const idsToSelect = this.selectedIds;

    // 2. Filter the current selection to remove the ids that were deselected
    const draftSelection = this.plate.snapshot.selection.filter((blockId) => !idsToDeselect.includes(blockId));

    // 3. Add the ids that were selected to the draft selection
    const finalSelection = [...draftSelection, ...idsToSelect];

    // 4. Update the selection
    this.plate.select(finalSelection, { addToStack: false });
  }
}

/* -------------------------------------------------- */
/*                       RESIZE                       */
/* -------------------------------------------------- */

export class ResizeCommand extends BaseCommand {
  readonly type = "RESIZE";

  readonly blocks: {
    id: string;
    from: {
      width: number;
      height: number;
    };
    to: {
      width: number;
      height: number;
    };
  }[];

  plate: Plate;

  constructor(resizeSession: ResizeSession, plate: Plate) {
    super();

    this.plate = plate;

    this.blocks = resizeSession.initialBlockSnapshots.map((b) => {
      const block = this.plate.getBlockById(b.id)!;
      const newWidth = block.snapshot.width;
      const newHeight = block.snapshot.height;

      const prevWidth = b.width;
      const prevHeight = b.height;

      return {
        id: b.id,
        from: { width: prevWidth, height: prevHeight },
        to: { width: newWidth, height: newHeight },
      };
    });
  }

  undo(): void {
    this.blocks.forEach((blockPosition) => {
      const block = this.plate.getBlockById(blockPosition.id)!;
      block.resize(blockPosition.from.width, blockPosition.from.height);
    });
  }

  redo(): void {
    this.blocks.forEach((blockPosition) => {
      const block = this.plate.getBlockById(blockPosition.id)!;
      block.resize(blockPosition.to.width, blockPosition.to.height);
    });
  }

  /**
   * Checks if new and old sizes are equal
   */
  blocksResized(): boolean {
    return (
      this.blocks[0].to.width !== this.blocks[0].from.width ||
      this.blocks[0].to.height !== this.blocks[0].from.height
    );
  }
}

/* -------------------------------------------------- */
/*                       CREATE                       */
/* -------------------------------------------------- */

export class CreateCommand extends BaseCommand {
  readonly type = "CREATE";

  readonly blocks: Block[];

  plate: Plate;

  constructor(blocks: Block[], plate: Plate) {
    super();

    this.blocks = blocks;
    this.plate = plate;
  }

  undo(): void {
    this.plate.deleteBlocks(
      this.blocks.map((b) => b.snapshot.id),
      false,
      true
    );
  }

  redo(): void {
    const duplicatedSnapshots = this.blocks.map((block) => ({ ...block.snapshot, id: uuidv4() }));
    const blocks = duplicatedSnapshots.map((snapshot) => BlockFactory.create(snapshot, this.plate.socket));
    this.plate.createBlocks(blocks, false, true);
  }
}

/* -------------------------------------------------- */
/*                       DELETE                       */
/* -------------------------------------------------- */

export class DeleteCommand extends BaseCommand {
  readonly type = "DELETE";

  readonly blocks: Block[];

  plate: Plate;

  constructor(blocks: Block[], plate: Plate) {
    super();

    this.blocks = blocks;
    this.plate = plate;
  }

  undo(): void {
    const duplicatedSnapshots = this.blocks.map((block) => ({ ...block.snapshot, id: uuidv4() }));
    const blocks = duplicatedSnapshots.map((snapshot) => BlockFactory.create(snapshot, this.plate.socket));
    this.plate.createBlocks(blocks, false, true);
  }

  redo(): void {
    this.plate.deleteBlocks(
      this.blocks.map((b) => b.snapshot.id),
      false,
      true
    );
  }
}
